// frontend/src/lib/posthog.js
//
// Lazy, consent-gated PostHog loader. Mirrors lib/sentry.js: a proxy object
// (`analytics`) buffers calls until the real posthog-js SDK dynamic-imports,
// keeping the SDK out of the entry chunk (protects FCP/LCP on the landing
// route, which has no analytics to send and no user to identify).
//
// Differs from Sentry in two GDPR-driven ways:
//   1. EU residency — defaults to the EU ingestion host.
//   2. Consent-gated — NOTHING is captured, buffered, or even loaded until the
//      user has accepted the cookie banner (localStorage `cookie_consent_*`).
//      Before consent, every analytics call is a silent no-op (not buffered),
//      so no pre-consent navigation can leak once the SDK finally boots.
//
// No-op entirely when VITE_POSTHOG_KEY is unset (dev / test / self-host).

// Same key the CookieBanner writes on "Accept".
const CONSENT_KEY = 'cookie_consent_dismissed'
const KEY  = import.meta.env.VITE_POSTHOG_KEY
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'

let real = null          // resolved posthog-js default export, once init succeeds
let initPromise = null   // dedupes concurrent / repeated init attempts
const buffered = []      // {fn, args} queued post-consent, pre-SDK-ready

function hasConsent() {
  try {
    return !!localStorage.getItem(CONSENT_KEY)
  } catch {
    // Private-mode / blocked storage — treat as no consent (fail closed).
    return false
  }
}

function flush() {
  if (!real) return
  while (buffered.length) {
    const { fn, args } = buffered.shift()
    try {
      const target = real[fn]
      if (typeof target === 'function') target.apply(real, args)
    } catch {
      // Never let an analytics call mask the original app flow.
    }
  }
}

// Dynamically import + initialise posthog-js. No-op unless a key is configured
// AND the user has consented. Safe to call repeatedly (deduped via initPromise).
export function initPostHog() {
  if (!KEY || !hasConsent() || initPromise) return initPromise
  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      try {
        posthog.init(KEY, {
          api_host: HOST,
          // Only create person profiles for users we explicitly identify()
          // — anonymous visitors stay anonymous (cheaper, more private).
          person_profiles: 'identified_only',
          // SPA: we send $pageview manually on route change (see App.tsx).
          capture_pageview: false,
          // No session replay — avoids recording PII-laden production data.
          disable_session_recording: true,
          // Autocapture stays on for product insight, but all text is masked
          // so element labels (production names, people) never leave the client.
          autocapture: true,
          mask_all_text: true,
          mask_all_element_attributes: true,
        })
        real = posthog
        flush()
      } catch {
        // init threw (e.g. partial mock in tests, or SDK error) — leave the
        // proxy buffering; the app keeps working without analytics.
      }
      return real
    })
    .catch(() => {
      // Dynamic import failed (offline, blocked) — analytics simply absent.
    })
  return initPromise
}

function call(fn, args) {
  if (real) {
    try {
      const target = real[fn]
      if (typeof target === 'function') target.apply(real, args)
    } catch {
      // Best-effort — SDK may be in a degraded state.
    }
    return
  }
  // No live SDK yet. Only buffer (and kick off init) once consent exists —
  // otherwise drop the call so nothing pre-consent is ever sent.
  if (KEY && hasConsent()) {
    buffered.push({ fn, args })
    initPostHog()
  }
}

// Public proxy. Surface mirrors the posthog-js methods the app actually uses.
export const analytics = {
  identify: (...args) => call('identify', args),
  group:    (...args) => call('group', args),
  capture:  (...args) => call('capture', args),
  reset:    (...args) => call('reset', args),
  // Called by CookieBanner the moment the user accepts — boots the SDK now so
  // the rest of the session is tracked without waiting for a reload.
  enable:   () => initPostHog(),
}

// Auto-boot for a returning, already-consented user (accepted in a prior
// visit). New visitors boot later via analytics.enable() on banner accept.
if (KEY && hasConsent()) initPostHog()
